import { useEffect, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { CircleNotch, CheckCircle, ShieldCheck, HourglassLow, HandPalm } from "@phosphor-icons/react";

const label = { color: "var(--bk-text-2)" };

export default function PublicWaitlistClaim() {
  const { slug, waitlistId } = useParams();
  const [params] = useSearchParams();
  const token = params.get("t") || "";
  const [offer, setOffer] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [ack, setAck] = useState(false);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  useEffect(() => {
    api.get(`/public/booking/${slug}/claim/${waitlistId}`, { params: { token } })
      .then((r) => setOffer(r.data)).catch(() => setNotFound(true));
  }, [slug, waitlistId, token]);

  const act = async (action) => {
    if (action === "claim" && !ack) return toast.error("Please confirm the safety acknowledgment first.");
    setBusy(true);
    try {
      const { data } = await api.post(`/public/booking/${slug}/claim/${waitlistId}/${action}`, { token, safety_ack: ack });
      setResult({ msg: data.rowan_message, manage: data.manage_path });
    } catch (e) { toast.error(e?.response?.data?.detail || "Couldn't do that."); }
    finally { setBusy(false); }
  };

  if (notFound) return (
    <div className="min-h-screen flex items-center justify-center" style={{ background: "#F8F7F4" }}>
      <p className="text-base" style={label} data-testid="claim-notfound">We couldn't find that offer.</p>
    </div>
  );
  if (!offer) return (
    <div className="min-h-screen flex items-center justify-center" style={{ background: "#F8F7F4" }}>
      <CircleNotch size={28} className="animate-spin" color="var(--bk-accent)" />
    </div>
  );

  if (result) return (
    <div className="min-h-screen px-4 py-16" style={{ background: "#F8F7F4" }}>
      <div className="max-w-xl mx-auto bk-card p-8 text-center" data-testid="claim-result">
        <CheckCircle size={44} weight="fill" color="var(--bk-success)" className="mx-auto" />
        <p className="text-base mt-4" style={{ color: "var(--bk-text)" }}>{result.msg}</p>
        {result.manage && (
          <a href={result.manage} className="inline-block mt-4 text-sm font-semibold underline" style={{ color: "var(--bk-accent)" }} data-testid="claim-manage-link">Manage your booking →</a>
        )}
      </div>
    </div>
  );

  if (offer.expired || offer.status === "expired" || offer.status === "declined") return (
    <div className="min-h-screen px-4 py-16" style={{ background: "#F8F7F4" }}>
      <div className="max-w-xl mx-auto bk-card p-8 text-center" data-testid="claim-expired">
        <HourglassLow size={40} weight="duotone" color="#B08840" className="mx-auto" />
        <p className="text-base mt-4" style={{ color: "var(--bk-text)" }}>This offer has {offer.status === "declined" ? "been declined" : "expired"} — the spot went to the next person in line. We'll email you if another one opens.</p>
        <a href={`/book/${slug}`} className="bk-btn-primary inline-block px-5 py-2.5 rounded-full text-sm font-semibold mt-6">Book another time</a>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen px-4 py-12" style={{ background: "#F8F7F4" }} data-testid="claim-page">
      <div className="max-w-xl mx-auto space-y-5">
        <div className="text-center">
          <h1 className="font-display text-2xl font-bold" style={{ color: "var(--bk-text)" }}>{offer.business_name}</h1>
          <p className="text-sm mt-1" style={label}>A spot opened up for you, {offer.client_name}!</p>
        </div>
        <div className="bk-card p-6" data-testid="claim-offer-card">
          <p className="font-semibold" style={{ color: "var(--bk-text)" }}>{offer.service_name}</p>
          <p className="text-sm mt-1" style={label}>{offer.date} · {offer.start_time}{offer.end_time ? `–${offer.end_time}` : ""}</p>
          {offer.offer_expires_at && <p className="text-xs mt-1 font-semibold" style={{ color: "#B08840" }} data-testid="claim-expiry">Offer expires {new Date(offer.offer_expires_at).toLocaleString()}</p>}
          <div className="rounded-xl border px-4 py-3 mt-4" style={{ borderColor: "rgba(232,168,56,0.5)", background: "#FFF9F0" }}>
            <label className="flex items-start gap-2.5 text-sm cursor-pointer" style={{ color: "var(--bk-text)" }}>
              <input type="checkbox" checked={ack} onChange={(e) => setAck(e.target.checked)} data-testid="claim-safety-ack" className="mt-0.5" />
              <span><ShieldCheck size={15} weight="fill" color="#E8A838" className="inline mr-1" />{offer.safety_text}</span>
            </label>
          </div>
          <div className="flex flex-wrap gap-2 mt-4">
            <button onClick={() => act("claim")} disabled={busy || !ack} data-testid="claim-btn"
              className="bk-btn-primary inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-semibold disabled:opacity-50">
              {busy ? <CircleNotch size={15} className="animate-spin" /> : <CheckCircle size={15} weight="bold" />} Claim this spot
            </button>
            <button onClick={() => act("decline")} disabled={busy} data-testid="decline-btn"
              className="inline-flex items-center gap-1.5 px-4 py-2.5 rounded-full text-sm font-semibold border disabled:opacity-50" style={{ borderColor: "var(--bk-border)", color: "var(--bk-text-2)" }}>
              <HandPalm size={15} weight="bold" /> Pass — give it to the next person
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
